import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Rocket,
  GitBranch,
  BarChart3,
  Settings,
  LogOut,
  Workflow,
  MessageSquare,
  TrendingUp,
  LifeBuoy,
  LucideIcon,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useMyMenuPermissions, useMenuPermissionsRealtime, MenuKey } from '../hooks/useMenuPermissions';

interface NavItem {
  icon: LucideIcon;
  label: string;
  to: string;
  permKey?: MenuKey;
  comingSoon?: boolean;
  planGated?: boolean;
  adminHidden?: boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const sections: NavSection[] = [
  {
    label: 'DISCOVER',
    items: [
      { icon: LayoutDashboard, label: 'Dashboard',  to: '/app/dashboard', permKey: 'dashboard' },
      { icon: Users,           label: 'Leads',       to: '/app/leads',     permKey: 'leads' },
    ],
  },
  {
    label: 'ENGAGE',
    items: [
      { icon: Workflow,      label: 'Workflow Builder', to: '/app/workflows',  permKey: 'workflows' },
      { icon: Rocket,        label: 'Campaigns',        to: '/app/campaigns',  permKey: 'campaigns',  planGated: true },
      { icon: GitBranch,     label: 'Sequences',        to: '/app/sequences',  permKey: 'sequences',  planGated: true },
      { icon: MessageSquare, label: 'Replies',          to: '/app/replies',    permKey: 'replies',    planGated: true },
    ],
  },
  {
    label: 'ANALYZE',
    items: [
      { icon: BarChart3, label: 'Analytics', to: '/app/analytics', permKey: 'analytics' },
    ],
  },
  {
    label: 'SETTINGS',
    items: [
      { icon: TrendingUp, label: 'Upgrade Plan', to: '/app/settings?tab=Billing', permKey: 'settings_billing', adminHidden: true },
      { icon: Settings,   label: 'Settings',     to: '/app/settings' },
      { icon: LifeBuoy,   label: 'Support',       to: '/app/support' },
    ],
  },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { data: myPermsData } = useMyMenuPermissions();
  useMenuPermissionsRealtime();

  // Owners always see everything; others filtered by their allowed_menus
  const allowedMenus: Set<MenuKey> = new Set(
    myPermsData?.data?.allowed_menus ?? []
  );
  const isOwner = user?.orgRole === 'owner';
  const canSee = (permKey?: MenuKey) => !permKey || isOwner || allowedMenus.has(permKey);

  const isTrialExpired =
    user?.planStatus === 'trialing' &&
    !!user?.organization?.trial_ends_at &&
    new Date() > new Date(user.organization.trial_ends_at);
  const isPlanDeactivated =
    user?.planStatus === 'cancelled' || user?.planStatus === 'unpaid';
  const isSubscriptionExpired =
    user?.planStatus === 'active' &&
    !!user?.organization?.subscription_ends_at &&
    new Date() > new Date(user.organization.subscription_ends_at);
  const hasGoldPlusAccess =
    !!(user?.organization?.plan_features?.campaign_features) &&
    !isTrialExpired &&
    !isPlanDeactivated &&
    !isSubscriptionExpired;
  const isAdminPlan = user?.organization?.plan === 'admin';

  const isActive = (to: string) => {
    const [toPath, toSearch] = to.split('?');
    const pathMatches = location.pathname === toPath || location.pathname.startsWith(toPath + '/');
    if (!pathMatches) return false;
    if (toSearch) {
      // Link has query params — require all of them to match current URL
      const toParams = new URLSearchParams(toSearch);
      const curParams = new URLSearchParams(location.search);
      return [...toParams.entries()].every(([k, v]) => curParams.get(k) === v);
    }
    // Link has no query params — defer to a more-specific sibling if one matches
    const moreSpecificActive = sections.flatMap(s => s.items).some(item => {
      if (!item.to.includes('?')) return false;
      const [p, q] = item.to.split('?');
      if (p !== toPath) return false;
      const pParams = new URLSearchParams(q);
      const curParams = new URLSearchParams(location.search);
      return [...pParams.entries()].every(([k, v]) => curParams.get(k) === v);
    });
    return !moreSpecificActive;
  };

  const initials = user
    ? (user.firstName?.[0] || '') + (user.lastName?.[0] || '')
    : 'U';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside className={`portal-sidebar ${isOpen ? 'open' : ''}`}>
      <div className="sb-logo-wrap">
        <Link to="/" onClick={onClose}>
          <img src="/images/logo.png" alt="LeadNova" className="sb-logo" />
        </Link>
      </div>

      <nav className="sb-nav">
        {sections.map((section) => {
          const visibleItems = section.items.filter(
            item =>
              canSee(item.permKey) &&
              (!item.planGated || hasGoldPlusAccess) &&
              !(item.adminHidden && isAdminPlan)
          );
          if (visibleItems.length === 0) return null;
          return (
          <div key={section.label}>
            <div className="sb-section-label">{section.label}</div>
            {visibleItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.to);

              if (item.comingSoon) {
                return (
                  <div key={item.label} className="sb-nav-item sb-nav-disabled">
                    <Icon size={18} />
                    <span>{item.label}</span>
                    <span className="sb-soon-badge">Soon</span>
                  </div>
                );
              }

              return (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={onClose}
                  className={`sb-nav-item ${active ? 'sb-nav-active' : ''}`}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
          );
        })}
      </nav>

      <div className="sb-user-section">
        <div className="sb-avatar">
          {user?.avatarUrl
            ? <img src={user.avatarUrl} alt={user.fullName || initials} className="sb-avatar-img" />
            : (initials.toUpperCase() || 'U')}
        </div>
        <div className="sb-user-info">
          <div className="sb-user-name">{user?.fullName || 'User'}</div>
          {!isAdminPlan && (
            <div className="sb-plan-badge">
              {user?.planStatus === 'trialing'
                ? 'Trial'
                : `${user?.plan || 'Gold'} Plan`}
            </div>
          )}
        </div>
        <button onClick={handleLogout} className="sb-logout-btn" title="Log out" aria-label="Log out">
          <LogOut size={16} color="var(--gray-500)" />
        </button>
      </div>
      <style>{`
        .portal-sidebar {
          width: 240px;
          min-height: 100vh;
          background: var(--navy);
          display: flex;
          flex-direction: column;
          position: fixed;
          top: 0;
          left: 0;
          bottom: 0;
          z-index: 100;
        }
        .sb-logo-wrap { padding: 20px 20px 24px; }
        .sb-logo { height: 40px; }
        .sb-nav { flex: 1; overflow-y: auto; padding: 0 12px; }
        .sb-section-label {
          font-size: 11px; font-weight: 600; color: var(--gray-500);
          letter-spacing: 1px; padding: 20px 8px 8px;
        }
        .sb-nav-item {
          display: flex; align-items: center; gap: 12px;
          padding: 10px 12px; border-radius: 6px; font-size: 14px;
          font-weight: 500; color: var(--gray-200); text-decoration: none;
          transition: all 0.15s; margin-bottom: 2px;
          border-left: 3px solid transparent;
        }
        .sb-nav-item:hover { text-decoration: none; color: var(--white); }
        .sb-nav-active {
          background: var(--navy-light); color: var(--white);
          border-left-color: var(--sky);
        }
        .sb-nav-disabled { opacity: 0.4; cursor: not-allowed; }
        .sb-soon-badge {
          font-size: 10px; font-weight: 600;
          background: rgba(255,255,255,0.1); color: var(--gray-500);
          padding: 2px 6px; border-radius: 4px; margin-left: auto;
        }
        .sb-user-section {
          padding: 16px 20px; border-top: 1px solid rgba(255,255,255,0.1);
          display: flex; align-items: center; gap: 12px;
        }
        .sb-avatar {
          width: 36px; height: 36px; border-radius: 50%;
          background: var(--sky); display: flex; align-items: center;
          justify-content: center; color: var(--white);
          font-size: 14px; font-weight: 600; flex-shrink: 0; overflow: hidden;
        }
        .sb-avatar-img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
        .sb-user-info { flex: 1; min-width: 0; }
        .sb-user-name {
          font-size: 13px; font-weight: 600; color: var(--white);
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .sb-plan-badge {
          font-size: 10px; font-weight: 600; color: var(--amber);
          text-transform: uppercase; letter-spacing: 0.5px;
        }
        .sb-logout-btn {
          background: none; border: none; padding: 4px;
          margin-left: auto; cursor: pointer; display: flex;
        }

        @media (max-width: 768px) {
          .portal-sidebar {
            transform: translateX(-100%);
            transition: transform 0.3s ease;
          }
          .portal-sidebar.open {
            transform: translateX(0);
          }
        }
      `}</style>
    </aside>
  );
}
